import { useEffect, useState } from 'react';
import {
  IonFooter,
  IonAccordion,
  IonItem,
  IonLabel,
  IonAccordionGroup,
  AccordionGroupCustomEvent,
  IonModal,
} from '@ionic/react';
import FilterInput from '../FilterInput/FilterInput';
import { FilterValues } from '../../api/types/generated/report_data_post.content';
import './FilterForm.css';
import Button from '../Button/Button';
import Message from '../Message/Message';
import ArrowIcon from '../ArrowIcon/ArrowIcon';
import { getDateFilterValuesLabel } from '../../utils/filters/getDateFilterValuesLabel';
import { getDefaultFilterData } from '../../utils/filters/getDefaultFilterData';
import { useTranslate } from '../../hooks/context/useTranslate';
import Popover from '../Popover/Popover';
import { Filter } from '../../types/types';

export type FilterData = {
  id: string;
  values?: FilterValues;
  namedRange?: string;
};

interface IFilterFormProps {
  onSubmit: (values: FilterData[]) => void;
  filters?: Filter[];
  values: FilterData[];
  popover?: boolean;
  buttonText?: string;
}

export default function FilterForm({
  onSubmit,
  filters,
  values,
  popover,
  buttonText,
}: IFilterFormProps) {
  const { t } = useTranslate();
  const [localValues, setLocalValues] = useState<FilterData[]>(values);
  const [accordionValue, setAccordionValue] = useState(
    filters && filters.length === 1 ? filters[0].id : undefined
  );
  const [isOpen, setIsOpen] = useState(false);

  const handleSave = () => {
    setIsOpen(false);
    if (localValues !== values) {
      onSubmit(localValues);
    }
  };

  const handleClear = () => {
    const values: FilterData[] = [];
    filters?.forEach((filter) => {
      const defaultData = getDefaultFilterData(filter);
      if (defaultData) {
        values.push(defaultData);
      }
    });
    setLocalValues(values);
  };

  useEffect(() => {
    setLocalValues(values);
  }, [values]);

  const handleChange = (filterData: FilterData) => {
    const newValues = [...localValues];
    const index = newValues.findIndex(({ id }) => id === filterData.id);
    let newValue = filterData.values || [];
    const valueEmpty = newValue.every(
      (element) => element == null || element == undefined || element === ''
    );
    if (valueEmpty) {
      newValue = [];
    }
    if (index === -1) {
      if (!valueEmpty) {
        newValues.push({
          ...filterData,
          values: newValue,
        });
      }
    } else {
      if (valueEmpty) {
        newValues.splice(index, 1);
      } else {
        newValues[index] = {
          ...filterData,
          values: newValue,
        };
      }
    }
    setLocalValues(newValues);
  };

  const accordionToggle = (event: AccordionGroupCustomEvent) => {
    if (event.target.tagName === 'ION-ACCORDION-GROUP') {
      setAccordionValue(event.detail.value);
    }
  };

  const getFilterValuesLabel = (filterData: FilterData, filter: Filter) => {
    if (!filterData.values) {
      return '';
    }
    switch (filter.type) {
      case 'DATE':
      case 'DATERANGE':
        return getDateFilterValuesLabel(filterData, filter.type, t);
      case 'STRING':
      case 'NUMBER':
        return filterData.values[0];
      case 'NUMBERRANGE':
        return `${filterData.values[0]} - ${filterData.values[1]}`;
      case 'SELECT':
        const firstValue = filterData.values[0];
        const firstValueItem = filter.availableValues.find(
          (filter) => filter.key === firstValue
        );
        return firstValueItem ? firstValueItem.label : '';
      case 'MULTISELECT':
        if (filterData.values.length < 6) {
          return filterData.values
            .map((value) => {
              const foundValue = filter.availableValues.find(
                (filter) => filter.key === value
              );
              return foundValue ? foundValue.label : '';
            })
            .join(', ');
        } else {
          const numberOfItems = (filterData.values.length - 1).toString();
          const firstValue = filterData.values[0];
          const firstValueItem = filter.availableValues.find(
            (filter) => filter.key === firstValue
          );
          return `${firstValueItem?.label} + ${t('otherValues').replace('{number}', numberOfItems)}`;
        }
        return '';
      default:
        return '';
    }
  };

  const content = (
    <>
      <div className="filters-container">
        {filters && (
          <>
            {filters.length === 1 ? (
              <>
                <IonItem className="filter-header-item" lines="none">
                  <IonLabel className="filter-label">
                    {filters[0].label}
                  </IonLabel>
                </IonItem>
                <FilterInput
                  onChange={handleChange}
                  filter={filters[0]}
                  filterData={localValues.find(
                    ({ id }) => id === filters[0].id
                  )}
                />
              </>
            ) : (
              <IonAccordionGroup
                multiple={false}
                value={accordionValue}
                onIonChange={accordionToggle}
              >
                {filters.map((filter) => {
                  const filterValues = localValues.find(
                    ({ id }) => id === filter.id
                  );
                  const valuesText = filterValues
                    ? getFilterValuesLabel(filterValues, filter)
                    : '';
                  return (
                    <IonAccordion
                      key={filter.id}
                      value={filter.id}
                      toggleIcon={undefined}
                    >
                      <IonItem
                        className="filter-header-item"
                        slot="header"
                        lines="none"
                      >
                        <IonLabel className="filter-label">
                          {filter.label}
                          {filterValues && (
                            <span className="values-text">{valuesText}</span>
                          )}
                        </IonLabel>
                        <ArrowIcon
                          direction="down"
                          className="ion-accordion-toggle-icon"
                        />
                      </IonItem>
                      <div slot="content">
                        <FilterInput
                          onChange={handleChange}
                          filter={filter}
                          filterData={filterValues}
                        />
                      </div>
                    </IonAccordion>
                  );
                })}
              </IonAccordionGroup>
            )}
          </>
        )}
      </div>

      <IonFooter>
        <Button onClick={handleClear} small secondary>
          <Message id="clearFilter" />
        </Button>
        <Button onClick={handleSave} small>
          <Message id="submit" />
        </Button>
      </IonFooter>
    </>
  );

  if (popover) {
    return (
      <Popover
        isOpen={isOpen}
        className="filter filter-popover"
        onClickOutside={handleSave}
        align="end"
        showBackdrop
        content={content}
      >
        <div slot="end" className="filter-button">
          <Button onClick={() => setIsOpen(true)}>{buttonText}</Button>
        </div>
      </Popover>
    );
  }

  return (
    <>
      <Button onClick={() => setIsOpen(true)} small>
        {buttonText}
      </Button>
      <IonModal
        isOpen={isOpen}
        onWillDismiss={handleSave}
        className="filter filter-modal"
      >
        {content}
      </IonModal>
    </>
  );
}
